import { apiService } from './api';

export type SupportCaseType = 'issue' | 'question' | 'feature' | 'incident';
export type SupportPriority = 'low' | 'normal' | 'high';
export type SupportStatus =
  | 'new'
  | 'triaged'
  | 'in_progress'
  | 'waiting_on_customer'
  | 'resolved'
  | 'closed';

export interface SupportAttachment {
  key: string;
  filename: string;
  contentType?: string;
  downloadUrl?: string | null;
}

export interface SupportMessage {
  messageId: string;
  authorKind: 'customer' | 'support' | string;
  authorName?: string;
  body: string;
  createdAt: string;
  attachments: SupportAttachment[];
}

export interface SupportCaseSummary {
  ref: string;
  title: string;
  description?: string;
  status: SupportStatus;
  type: SupportCaseType;
  priority: SupportPriority;
  createdAt: string;
  updatedAt: string;
}

export interface SupportCase extends SupportCaseSummary {
  messages: SupportMessage[];
}

export interface SupportUpload {
  s3Key: string;
  filename: string;
  contentType: string;
  byteSize: number;
}

interface UploadTarget {
  uploadUrl: string;
  s3Key: string;
  fileName: string;
  contentType: string;
}

const ALLOWED_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/zip',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

export const SUPPORT_FILE_LIMIT = 8_000_000;
export const SUPPORT_FILE_COUNT = 5;

export function supportFileError(file: File): string | null {
  const type = (file.type || '').toLowerCase();
  if (!ALLOWED_TYPES.has(type)) return 'Use a PNG, JPEG, GIF, WEBP, PDF, text, CSV, ZIP, DOCX or XLSX file.';
  if (file.size > SUPPORT_FILE_LIMIT) return 'Each file must be under 8 MB.';
  return null;
}

export function isSupportUnavailable(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error || '');
  return message.includes('503') || message.includes('support_unavailable');
}

export const supportService = {
  async listCases(): Promise<SupportCaseSummary[]> {
    const result = await apiService.customRequest('/support/cases');
    return result?.data?.cases || [];
  },

  async getCase(ref: string): Promise<SupportCase> {
    const result = await apiService.customRequest(`/support/cases/${encodeURIComponent(ref)}`);
    return result?.data?.case;
  },

  async createCase(input: {
    type: SupportCaseType;
    title: string;
    description: string;
    priority: SupportPriority;
    idempotencyKey: string;
    featureId?: string;
    context?: { url?: string; appVersion?: string; userAgent?: string };
  }): Promise<{ ref: string; status: SupportStatus; replayed: boolean }> {
    const result = await apiService.customRequest('/support/cases', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return result?.data;
  },

  async reply(ref: string, input: { body: string; uploads?: SupportUpload[] }): Promise<SupportMessage> {
    const result = await apiService.customRequest(`/support/cases/${encodeURIComponent(ref)}/messages`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return result?.data?.message;
  },

  async requestUploadUrl(file: File): Promise<UploadTarget> {
    const result = await apiService.customRequest('/support/uploads/upload-url', {
      method: 'POST',
      body: JSON.stringify({
        fileName: file.name,
        contentType: file.type,
        size: file.size,
      }),
    });
    return result?.data;
  },

  async uploadToS3(target: UploadTarget, file: File): Promise<SupportUpload> {
    const response = await fetch(target.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': target.contentType },
      body: file,
    });
    if (!response.ok) throw new Error(`Upload failed (${response.status})`);
    return {
      s3Key: target.s3Key,
      filename: target.fileName,
      contentType: target.contentType,
      byteSize: file.size,
    };
  },
};
